/**
 * EWBCI API Client の型定義
 */

import type { FileRequestInput, ItpcmdRequestInput, SubscribeRequestInput } from "@/feature/api-client";
type GetDGTRequestInput = '{}';

/**
 * API タイプの定義
 */
export type ApiType = 'getdgt' | 'itpcmd' | 'file' | 'subscribe';

/**
 * EWBCI リクエスト型
 */
export type EWBCIRequestMessage = EWBCIGetDGTRequest | EWBCIItpcmdRequest | EWBCIFileRequest | EWBCISubscribeRequest;

/**
 * EWBCI Requestメッセージベース型
 */
type _EWBCIRequestBase = {
    sessionId: string;
    requestId: string;
    serialId: string;
    deviceId?: string;
};

/**
 * EWBCI GetDGT リクエスト型
 */
export type EWBCIGetDGTRequest = _EWBCIRequestBase & {
    getdgt: GetDGTRequestInput;
};

/**
 * EWBCI Itpcmd リクエスト型
 */
export type EWBCIItpcmdRequest = _EWBCIRequestBase & {
    itpcmd: ItpcmdRequestInput;
};

/**
 * EWBCI File リクエスト型
 */
export type EWBCIFileRequest = _EWBCIRequestBase & {
    file: FileRequestInput;
};

/**
 * EWBCI Subscribe リクエスト型
 */
export type EWBCISubscribeRequest = _EWBCIRequestBase & {
    subscribe: SubscribeRequestInput;
};


/**
 * EWBCI メッセージベース型
 */
type _EWBCIMessageBase = {
    userId: string;
    sessionId: string;
    requestId: string;
    requestedAt: string;
    serialId?: string;
    deviceId?: string;
};

/**
 * 共通レスポンスボディ型
 */
export type CommonResponseBody<T extends object | undefined = object> = {
    status: Number;
    reason?: string;
    body?: T;
};

/**
 * EWBCI チャンクメッセージ型
 */
export type EWBCIChunkedMessage = _EWBCIMessageBase & {
    __typename: 'EWBCIChunkedMessage';
    api: ApiType;
    body: {
        n: number; // 分割数
        i: number; // 枝番( [0,n-1] )
        d: string; // base64 encoded chunked data
    };
};

/**
 * EWBCI GetDGT レスポンス型
 */
export type EWBCIGetDGTResponse = _EWBCIMessageBase & {
    __typename: 'EWBCIGetDGTResponse';
    responsedAt: string;
    api: 'getdgt';
    body: CommonResponseBody<{ dgt: string }>;
};

/**
 * EWBCI Itpcmd レスポンス型
 */
export type EWBCIItpcmdResponse<T extends object = object> = _EWBCIMessageBase & {
    __typename: 'EWBCIItpcmdResponse';
    responsedAt: string;
    api: 'itpcmd';
    body: CommonResponseBody<T>;
};

/**
 * EWBCI File レスポンス型
 */
export type EWBCIFileResponse = _EWBCIMessageBase & {
    __typename: 'EWBCIFileResponse';
    responsedAt: string;
    api: 'file';
    body: CommonResponseBody<undefined>;
};

/**
 * EWBCI Subscribe レスポンス型
 */
export type EWBCISubscribeResponse = _EWBCIMessageBase & {
    __typename: 'EWBCISubscribeResponse';
    responsedAt: string;
    api: 'subscribe';
    body: CommonResponseBody<undefined>;
};

/**
 * EWBCI レスポンスメッセージ型（ユニオン型）
 */
export type EWBCIResponseMessage = EWBCIGetDGTResponse | EWBCIItpcmdResponse<any> | EWBCIFileResponse | EWBCISubscribeResponse;

/**
 * EWBCI Subscribe フォームイベント型
 */
export type EWBCISubscribeFormEvent = {
    type: "form";
    form: Record<string, string>;
};

/**
 * EWBCI Subscribe ページイメージイベント型
 */
export type EWBCISubscribePageImageEvent = {
    type: "pageImage";
    file: string; // base64 encoded svg image data
};

/**
 * EWBCI Subscribe エラーイベント型
 */
export type EWBCISubscribeErrorEvent = {
    type: "timeout" | "conflict";
    message: string;
};

/**
 * EWBCI Subscribe イベントボディ型
 */
export type EWBCISubscribeEventBody = {
    pageId: string;
    body: EWBCISubscribeFormEvent | EWBCISubscribePageImageEvent | EWBCISubscribeErrorEvent;
    meta: {
        subscribeRequestId: string;
        subscribeRequestedAt: string;
    };
};

/**
 * EWBCI イベントメッセージ型
 */
export type EWBCIEventMessage = _EWBCIMessageBase & {
    __typename: 'EWBCIEventMessage';
    api: 'subscribe';
    body: EWBCISubscribeEventBody;
};

/**
 * EWBCI メッセージ型（ユニオン型）
 */
export type EWBCIMessage = EWBCIChunkedMessage | EWBCIResponseMessage | EWBCIEventMessage;

/**
 * 接続状態を管理するための列挙型
 */
export const ConnectionStatus = {
    NONE: 'None', // 初期状態
    SERVER_DISCONNECTED: 'server_disconnected', // Lantana接続切断発生
    SERVER_CONNECTING: 'server_connecting', // Lantana接続試行中
    SERVER_CONNECTED: 'server_connected', // Lantana接続完了
    EWB_DISCONNECTED: 'ewb_disconnected', // eWB接続切断発生(Lantana接続は有効だが、eWB接続に切断が発生/再接続に失敗)
    EWB_CONNECTING: 'ewb_connecting', // eWB接続試行中(Lantanaとは接続しているが、eWBと接続試行中の状態)
    CONNECTED: 'connected', // 接続完了
    DISCONNECTED: 'disconnected', // 切断（終了状態）
};
export type ConnectionStatus = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

/**
 * EWBCI クライアントの設定オプション
 */
export interface EWBCIClientConfig {
    /** シリアルID */
    serialId: string;
    /** デバイスID */
    deviceId: string;
    /** テナントID */
    tenantId: string;
}

/**
 * メッセージ処理の設定
 */
export interface MessageProcessingConfig {
    /** タイムアウト時間（ミリ秒） */
    timeout: number;
    /** 最大リトライ回数 */
    maxRetries: number;
}


export function toLog(m: EWBCIMessage): string {
    switch (m.__typename) {
        case 'EWBCIGetDGTResponse':
            return `[getdgt](${m.requestId})`;
        case 'EWBCIItpcmdResponse':
        case 'EWBCIFileResponse':
        case 'EWBCISubscribeResponse':
            return `[${m.api}](${m.requestId}, ${m.body.status}, ${m.body.reason})`;
        case 'EWBCIChunkedMessage':
            return `[${m.api}](${m.requestId}, ${m.body.i}/${m.body.n})`;
        case 'EWBCIEventMessage':
            return `[event::${m.body.body.type}](${m.body.pageId}, ${m.requestId} <${m.body.meta.subscribeRequestId}>)`;
        default:
            return `[]Unknown EWBCIMessage type: ${(m as any).__typename}`;
    }
}

export type BlobURL = string;

export type ChangeConnectionStatusHandler = (status: ConnectionStatus) => void;

/**
 * ページ監視(subscription)の状態に関するイベント型
 */
export type PageSubscriptionEvent = {
    type: 'SUBSCRIBING',
    pageId: string,
} | {
    type: 'SUBSCRIBED',
    pageId: string,
} | {
    type: 'UNAVAILABLE',
    pageId: string,
} | {
    type: 'UNSUBSCRIBED',
    pageId: string,
}

/**
 * ページイベントハンドラー型定義
 */
export interface PageEventHandlers {
    /** サブスクリプション状態遷移イベントハンドラー */
    handleSubscriptionEvent?: (event: PageSubscriptionEvent) => void;
    /** フォーム変更イベントハンドラー */
    onChangePageForm?: (pageId: string, form: Record<string, string>, timestamp: Date) => void;
    /** ページ画像変更イベントハンドラー */
    onChangePageImage?: (pageId: string, image: string, timestamp: Date) => void;
}

/**
 * ページサブスクライブオプション型定義
 */
export interface SubscribePageOptions {
    /** ページID */
    pageId: string;
    /** リクエストID（省略可） */
    requestId?: string;
    /** イベントハンドラー */
    handlers: PageEventHandlers;
}
